export type ApiOptions = RequestInit & { token?: string };
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}
export async function api<T>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.token) headers.set("Authorization", `Bearer ${options.token}`);
  if (options.body && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  const controller = options.signal ? null : new AbortController();
  const timeout = controller ? window.setTimeout(() => controller.abort(), 15000) : null;
  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers,
      cache: "no-store",
      signal: options.signal || controller?.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new ApiError("请求超时，请检查网络后重试", 408);
    throw error;
  } finally {
    if (timeout !== null) window.clearTimeout(timeout);
  }
  const payload = (response.headers.get("content-type") || "").includes(
    "application/json",
  )
    ? await response.json()
    : await response.text();
  if (!response.ok)
    throw new ApiError(
      payload?.detail || payload || `请求失败 ${response.status}`,
      response.status,
    );
  return payload as T;
}
export const post = <T>(path: string, body: unknown, token: string) =>
  api<T>(path, { method: "POST", body: JSON.stringify(body), token });
export type Summary = {
  active_questions: number;
  pending_questions: number;
  knowledge_points: number;
  papers_today: number;
  print_batches_today: number;
  grading_today: number;
  grading_review: number;
  agent_running: number;
  pending_approvals: number;
  maintenance_pending: number;
  duplicate_review: number;
  import_review: number;
  weak_points: number;
};
export type AutomationPolicy = {
  id: string; campus_id: string; schedule_slot_id: string; enabled: boolean;
  profile_id?: string; inheritance_mode: "system" | "campus" | "preset" | "custom"; profile_name?: string;
  generation_mode: "class" | "personalized"; trigger_days_before: number; trigger_local_time: string;
  timezone: string; question_count: number; wrong_question_count: number;
  difficulty_offset: number; foundation_offset: number; frequency_offset: number;
  knowledge_strategy: string; knowledge_point_ids: string[]; approval_required: boolean;
  scope_id?: string; scope_name?: string; config?: {
    generation_basis?: "wrong_regeneration" | "knowledge_composition";
    new_question_axes?: Array<{ knowledge_point_id: string; weight: number; frequency: number; difficulty: number }>;
    expand_neighbor_knowledge?: boolean; neighbor_knowledge_limit?: number;
  };
  auto_dispatch_after_approval: boolean; print_copies: number; campus_name: string;
  class_name: string; course_name: string; teacher_name: string; place_name: string;
  weekday: number; start_time: string; end_time: string; latest_status?: string;
  latest_stage?: string; latest_lesson_date?: string; latest_updated_at?: string;
};
export type AutomationProfile = {
  id: string; campus_id?: string; campus_name?: string; profile_type: "system" | "campus" | "preset";
  name: string; description?: string; settings: Omit<AutomationPolicy, "id" | "campus_id" | "schedule_slot_id" |
    "campus_name" | "class_name" | "course_name" | "teacher_name" | "place_name" | "weekday" |
    "start_time" | "end_time" | "inheritance_mode">; assigned_count: number; updated_at: string;
};
export type AutomationSettings = {
  system?: AutomationProfile; campus_profiles: AutomationProfile[]; presets: AutomationProfile[];
  campuses: Array<{ id: string; name: string; policy_count: number; exception_count: number }>;
};
export type TeachingWorkflow = {
  id: string; policy_id: string; schedule_slot_id: string; class_group_id: string;
  lesson_date: string; status: string; current_stage: string; attempt_count: number;
  max_attempts: number; agent_run_id?: string; approval_id?: string; paper_id?: string;
  print_batch_id?: string; context?: Record<string, unknown>; metrics?: Record<string, unknown>;
  error?: { message?: string }; scheduled_at: string; started_at?: string; dispatched_at?: string;
  completed_at?: string; updated_at: string; campus_name: string; class_name: string; course_name: string;
};
export type AutomationControlPlane = {
  summary: { enabled_policies: number; lessons_today: number; running: number; approval_pending: number;
    ready_for_class: number; exceptions: number; completed_today: number };
  policies: AutomationPolicy[]; workflows: TeachingWorkflow[];
  stage_counts: Array<{ status: string; count: number }>;
};
export type QuestionSource = {
  id: string;
  name: string;
  provider: string;
  license_type: string;
  commercial_use_allowed: boolean;
  attribution_required: boolean;
  status: string;
  source_url?: string;
};
export type ImportBatch = {
  id: string;
  status: string;
  original_filename?: string;
  source_name: string;
  scope_name: string;
  subject: string;
  grade: string;
  total_count: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  imported_count: number;
  created_at: string;
  validation_summary?: { license_gate?: string; message?: string; unclassified_rows?: number };
};
export type MaintenanceSpace = {
  code: string;
  subject: string;
  collection_name: string;
  maintenance_batch_size: number;
  maintenance_max_wait_seconds: number;
  pending: number;
  batched: number;
  processing: number;
  review: number;
  failed: number;
  oldest_pending?: string;
  knowledge_points: number;
  questions: number;
  knowledge_vectors: number;
  question_vectors: number;
  duplicate_review: number;
};
export type Scope = {
  id: string;
  code: string;
  name: string;
  school_stage: string;
  grade: string;
  subject: string;
  publisher?: string;
  curriculum_version?: string;
  knowledge_count: number;
};
export type Knowledge = {
  id: string;
  code: string;
  name: string;
  depth: number;
  description?: string;
  question_count: number;
};
export type Question = {
  id: string;
  bank_entry_id: string;
  stable_code: string;
  version_no: number;
  question_type: string;
  difficulty: number;
  stem: { text: string };
  options: unknown[];
  answer: unknown;
  explanation: { text?: string } | string;
  max_score: number;
  tags: string[];
  status: string;
  quality_score: number;
  grade: string;
  subject: string;
  scope_name: string;
  created_by?: string;
  creator_name?: string;
  can_review?: boolean;
  review_notes?: string;
  knowledge_points: Array<{
    id: string;
    name: string;
    code: string;
    role: string;
    hit_strength: number;
    foundation_weight: number;
    weight_axis: number;
    frequency_score: number;
    knowledge_difficulty: number;
    confidence?: number;
    sample_size: number;
  }>;
};
export type Paper = {
  id: string;
  paper_code: string;
  title: string;
  version_no: number;
  total_score: number;
  status: string;
  generated_by: string;
  generated_at: string;
  class_group_id?: string;
  class_name?: string;
  student_name?: string;
  question_count: number;
  assessment_kind?: "quiz" | "routine_exam" | "formal_exam";
};
export type TeachingClassWorkspace = {
  id: string;
  campus_id: string;
  campus_name: string;
  name: string;
  course_name: string;
  subject?: string;
  grade?: string;
  teacher_name: string;
  capacity: number;
  active_students: number;
  paper_total: number;
  paper_today: number;
  pending_publish: number;
  waiting_grading: number;
  grading_review: number;
  attempts_30d: number;
  accuracy_30d?: number;
  weak_students: number;
  latest_paper_id?: string;
  latest_paper_title?: string;
  latest_paper_status?: string;
  latest_paper_at?: string;
};
export type TeachingPlanSummary = {
  id: string;
  scope_id: string;
  title: string;
  objective: string;
  period_start: string;
  period_end: string;
  status: string;
  version_no: number;
  scope_name: string;
  subject: string;
  grade: string;
  item_count: number;
  resolved_items: number;
};
export type TeachingClassDetail = {
  class: TeachingClassWorkspace & {
    head_teacher_name?: string;
    schedule_text?: string;
    opened_at?: string;
    schedule_end_at?: string;
    completed_lessons?: number;
    planned_lessons?: number;
  };
  roster: Array<{
    id: string;
    name: string;
    external_student_no?: string;
    grade?: string;
    enrollment_status: string;
    attempts: number;
    accuracy?: number;
    last_attempt_at?: string;
    mastery_points: number;
    mastery_avg?: number;
    weak_points: number;
    learning_layer: "no_evidence" | "support" | "steady" | "advanced";
  }>;
  knowledge: Array<{
    id: string;
    code: string;
    name: string;
    subject: string;
    grade: string;
    attempts: number;
    correct_attempts: number;
    accuracy?: number;
    student_count: number;
    last_evidence_at?: string;
  }>;
  plans: TeachingPlanSummary[];
  available_scopes: Array<{ id: string; name: string; subject: string; grade: string; knowledge_count: number }>;
  summary: { students: number; support: number; advanced: number; no_evidence: number };
};
export type TeachingPlanDetail = {
  plan: TeachingPlanSummary & { class_group_id: string; class_name: string; cancellation_reason?: string };
  items: Array<{
    id: string;
    knowledge_point_id: string;
    sequence_no: number;
    target_mastery: number;
    planned_sessions: number;
    status: string;
    evidence?: Record<string, unknown>;
    code: string;
    name: string;
    attempts: number;
    correct_attempts: number;
    accuracy?: number;
    student_count: number;
    mastery_avg?: number;
    mastery_students: number;
  }>;
  events: Array<{
    id: string;
    event_type: string;
    from_status?: string;
    to_status?: string;
    reason?: string;
    created_at: string;
    actor_name: string;
  }>;
};
export type PaperDetail = {
  paper: Paper & {
    scope_id: string;
    updated_at?: string;
    published_at?: string;
    closed_at?: string;
    cancelled_at?: string;
    lifecycle_note?: string;
    attempt_count: number;
    print_batch_count: number;
  };
  items: Array<{
    position: number;
    score: number;
    question_id: string;
    stable_code: string;
    version_no: number;
    question_type: string;
    stem: { text: string };
    attempt_count: number;
    correct_count: number;
  }>;
  events: Array<{
    id: string;
    event_type: string;
    from_status?: string;
    to_status?: string;
    reason?: string;
    created_at: string;
    actor_name: string;
  }>;
};
export type AgentRun = {
  id: string;
  run_type: string;
  status: string;
  attempt_count: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  creator_name?: string;
  target_stem?: { text?: string } | string;
  target_scope_name?: string;
  target_subject?: string;
  target_grade?: string;
  can_review?: boolean;
  approval_id?: string;
  approval_status?: string;
  action_type?: string;
  proposal?: Record<string, unknown>;
  model_call?: {
    provider?: string;
    model?: string;
    latency_ms?: number;
    usage?: Record<string, number>;
  };
  error?: { message?: string };
};
